import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  StatePanel,
  V2Avatar,
  V2Badge,
  V2Button,
  V2Card,
  V2EmptyState,
  V2Tabs,
} from '@sakhtar/design-system/react';
import { systemClock } from '@sakhtar/calendar';
import { amountColumnHeader } from '../../lib/finance-display.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { exactWhen, relativeWhen } from '../../lib/date/relativeWhen.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type {
  Customer360Response,
  CustomerOpeningReceivable,
  CustomerOrder,
  CustomerQuote,
} from '../../lib/types/customers.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { CustomerEditDialog } from './CustomerEditDialog.js';
import { CustomerCommunicationsPanel } from './CustomerCommunicationsPanel.js';
import { ActivitiesTab, ChangeHistoryTab, DealsTab, NotesTab } from './Customer360Tabs.js';
import {
  CopyButton,
  DocPanel,
  Field,
  KpiTile,
  Skeleton360,
  StatusBadge,
  UnavailablePanel,
  type DocColumn,
} from './Customer360Parts.js';
import {
  CUSTOMER_TABS,
  OPENING_AR_READ_CAP,
  PHONE_KIND_LABEL,
  initialsOf,
  isAtReadCap,
  isBeforeDay,
  orderStatusLabel,
  orderStatusTone,
  parseTab,
  quoteStatusLabel,
  quoteStatusTone,
  sumRial,
  type Customer360Tab,
} from './customer360Model.js';
import './customer360.css';

const fa = (n: number) => n.toLocaleString('fa-IR');

const KIND_LABEL = { person: 'شخص', organization: 'سازمان' } as const;

/** Single-line on purpose: ERP-266/274 guard asserts this exact wording (draft, not an invoice or final balance). */
const RECEIVABLE_DRAFT_NOTICE =
  'این موارد از فایل وارد شده‌اند و در انتظار تطبیق مالی هستند؛ فاکتور صادرشده، مانده قطعی دریافتنی یا سند ثبت‌شده محسوب نمی‌شوند.';

function phoneHref(scheme: 'tel' | 'sms', value: string): string {
  return `${scheme}:${value.replace(/[^\d+]/g, '')}`;
}

/**
 * Customer 360. Every number is derived from what the API returned (and what
 * the viewer may see); tabs the backend reports as unavailable stay honest
 * about it instead of showing placeholder data.
 */
export function CustomerDetailPage() {
  const { slug, ref } = useParams<{ slug: string; ref: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const base = slug ? tenantBasePath(slug) : '';
  const tab = parseTab(params.get('tab'));
  const { unit, formatAmount, label: unitLabel } = useAmountDisplay();
  const today = systemClock.today();

  const [editing, setEditing] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const response = useApiGet<Customer360Response>(
    slug && ref ? `${base}/api/v1/crm/customers/${encodeURIComponent(ref)}` : null,
    [slug, ref, refresh],
  );

  // Keep the last good payload on screen while «بروزرسانی» refetches.
  const [loaded, setLoaded] = useState<{ ref: string; data: Customer360Response } | null>(null);
  const fetched = response.status === 'ready' ? response.data : null;
  useEffect(() => {
    if (fetched && ref) setLoaded({ ref, data: fetched });
  }, [fetched, ref]);
  const data = loaded && loaded.ref === ref ? loaded.data : null;

  const tabsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    tabsRef.current
      ?.querySelector<HTMLElement>('[aria-selected="true"]')
      ?.scrollIntoView?.({ inline: 'center', block: 'nearest' });
  }, [tab, data]);

  if (!slug || !ref) return null;

  function selectTab(id: Customer360Tab) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id === 'overview') next.delete('tab');
        else next.set('tab', id);
        return next;
      },
      { replace: true },
    );
  }

  if (data === null) {
    if (response.status === 'error') {
      return (
        <StatePanel
          variant={response.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={
            response.error.status === 404
              ? 'مشتری پیدا نشد'
              : response.error.status === 403
                ? 'دسترسی ندارید'
                : 'پرونده مشتری در دسترس نیست'
          }
        />
      );
    }
    return <Skeleton360 />;
  }

  const { customer, actions, sections } = data;
  const profile = data.profile ?? {};
  const refreshing = response.status === 'loading';

  const quotes = sections.quotes.status === 'ready' ? (sections.quotes.items ?? []) : null;
  const orders = sections.orders.status === 'ready' ? (sections.orders.items ?? []) : null;
  const receivables =
    sections.invoicesAr.status === 'ready' ? (sections.invoicesAr.items ?? []) : null;
  const deals = sections.deals.status === 'ready' ? (sections.deals.items ?? []) : null;
  const activityItems =
    sections.activities.status === 'ready' ? (sections.activities.items ?? []) : null;
  const noteItems = sections.notes.status === 'ready' ? (sections.notes.items ?? []) : null;
  const openDeals = deals?.filter((d) => d.open) ?? [];

  const tabItems = CUSTOMER_TABS.map((t) => {
    const count =
      t.id === 'quotes'
        ? quotes?.length
        : t.id === 'orders'
          ? orders?.length
          : t.id === 'invoicesAr'
            ? receivables?.length
            : t.id === 'deals'
              ? deals?.length
              : t.id === 'activities'
                ? activityItems?.length
                : t.id === 'notes'
                  ? noteItems?.length
                  : undefined;
    const capped = t.id === 'invoicesAr' && count !== undefined && isAtReadCap(count);
    return {
      id: t.id,
      label: count === undefined ? t.label : `${t.label} (${fa(count)}${capped ? '+' : ''})`,
    };
  });

  // The opening-AR read is row-capped; at the cap its count/sum are partial, not totals.
  const receivablesCapped = receivables !== null && isAtReadCap(receivables.length);

  const activeOrders = orders?.filter((o) => o.status !== 'cancelled') ?? [];
  const location = [profile.province, profile.city].filter(Boolean).join('، ');

  const quoteColumns: readonly DocColumn<CustomerQuote>[] = [
    {
      key: 'ref',
      header: 'شماره',
      cardRole: 'title',
      render: (q) => <Link to={`${base}/crm/quotes/${encodeURIComponent(q.ref)}`}>{q.ref}</Link>,
    },
    {
      key: 'status',
      header: 'وضعیت',
      cardRole: 'badge',
      render: (q) => (
        <StatusBadge label={quoteStatusLabel(q.status)} tone={quoteStatusTone(q.status)} />
      ),
    },
    {
      key: 'total',
      header: amountColumnHeader('مبلغ', unit),
      align: 'end',
      render: (q) => formatAmount(q.totalRial),
    },
    {
      key: 'valid',
      header: 'اعتبار تا',
      render: (q) =>
        q.validUntil ? (
          <>
            {displayJalaliDate(q.validUntil)}{' '}
            {(q.status === 'sent' || q.status === 'draft') && isBeforeDay(q.validUntil, today) ? (
              <V2Badge tone="warn">اعتبار گذشته</V2Badge>
            ) : null}
          </>
        ) : (
          '—'
        ),
    },
  ];

  const orderColumns: readonly DocColumn<CustomerOrder>[] = [
    {
      key: 'ref',
      header: 'شماره',
      cardRole: 'title',
      render: (o) => <Link to={`${base}/crm/orders/${encodeURIComponent(o.ref)}`}>{o.ref}</Link>,
    },
    {
      key: 'status',
      header: 'وضعیت سفارش',
      cardRole: 'badge',
      render: (o) => (
        <StatusBadge label={orderStatusLabel(o.status)} tone={orderStatusTone(o.status)} />
      ),
    },
    {
      key: 'fulfillment',
      header: 'تحویل',
      render: (o) => (
        <StatusBadge
          label={o.fulfillment.statusLabel}
          tone={
            o.fulfillment.status === 'fulfilled'
              ? 'success'
              : o.fulfillment.status === 'partially_fulfilled'
                ? 'warn'
                : 'neutral'
          }
        />
      ),
    },
    {
      key: 'total',
      header: amountColumnHeader('مبلغ', unit),
      align: 'end',
      render: (o) => formatAmount(o.totalRial),
    },
    {
      key: 'source',
      header: 'فایل مبدأ',
      render: (o) =>
        o.importedSourceCode || o.importedBusinessDate ? (
          <>
            {o.importedSourceCode ? <span className="c360-ltr">{o.importedSourceCode}</span> : null}
            {o.importedSourceCode && o.importedBusinessDate ? ' · ' : null}
            {o.importedBusinessDate ? displayJalaliDate(o.importedBusinessDate) : null}
          </>
        ) : (
          '—'
        ),
    },
  ];

  const receivableColumns: readonly DocColumn<CustomerOpeningReceivable>[] = [
    {
      key: 'source',
      header: 'شماره در فایل مبدأ',
      cardRole: 'title',
      render: (r) => r.sourceReference,
    },
    {
      key: 'status',
      header: 'وضعیت',
      cardRole: 'badge',
      render: () => <V2Badge tone="neutral">پیش‌نویس؛ در انتظار تطبیق</V2Badge>,
    },
    {
      key: 'opening',
      header: 'تاریخ افتتاحیه',
      render: (r) => displayJalaliDate(r.openingDate),
    },
    {
      key: 'due',
      header: 'سررسید',
      render: (r) =>
        r.dueDate ? (
          <>
            {displayJalaliDate(r.dueDate)}{' '}
            {isBeforeDay(r.dueDate, today) ? <V2Badge tone="danger">سررسید گذشته</V2Badge> : null}
          </>
        ) : (
          'ثبت نشده'
        ),
    },
    {
      key: 'outstanding',
      header: amountColumnHeader('مانده', unit),
      align: 'end',
      render: (r) => formatAmount(r.outstandingAmountRial),
    },
  ];

  const contact = sections.contacts.status === 'ready' ? (sections.contacts.items?.[0] ?? {}) : {};
  const primaryContactRows = (
    [
      ['mobile', 'موبایل'],
      ['phone', 'تلفن'],
      ['email', 'ایمیل'],
    ] as const
  ).filter(([field]) => Object.hasOwn(contact, field));
  const hasContactPerson = Object.hasOwn(profile, 'contactPerson') && profile.contactPerson;
  const extraPhones = profile.extraPhones ?? [];
  const nothingToShowInContacts =
    primaryContactRows.length === 0 && !hasContactPerson && extraPhones.length === 0;

  return (
    <div className="c360">
      <div className="c360-head">
        <div className="c360-head__id">
          <V2Avatar initials={initialsOf(customer.name ?? customer.ref ?? '؟')} size="lg" />
          <div className="c360-head__text">
            <Link to={`${base}/crm/customers`} className="c360-crumb">
              <span aria-hidden="true">›</span> بازگشت به مشتریان
            </Link>
            <span className="c360-eyebrow">کارتابل مشتری</span>
            <h1 className="c360-title">{customer.name ?? customer.ref ?? 'مشتری'}</h1>
            <div className="c360-meta">
              {customer.status === 'active' ? (
                <V2Badge tone="success">فعال</V2Badge>
              ) : customer.status === 'inactive' ? (
                <V2Badge tone="neutral">غیرفعال</V2Badge>
              ) : null}
              {customer.kind ? <span>{KIND_LABEL[customer.kind]}</span> : null}
              {customer.ref ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                  <span className="ltr" dir="ltr">
                    {customer.ref}
                  </span>
                  <CopyButton text={customer.ref} label="کد مشتری" />
                </span>
              ) : null}
              {location ? <span>{location}</span> : null}
            </div>
          </div>
        </div>
        <div className="c360-actions">
          {actions.createQuote ? (
            <Link className="sk-btn sk-btn--secondary" to={`${base}/crm/quotes`}>
              پیش‌فاکتور جدید
            </Link>
          ) : null}
          {actions.createOrder ? (
            <Link className="sk-btn sk-btn--secondary" to={`${base}/crm/orders`}>
              سفارش جدید
            </Link>
          ) : null}
          <V2Button variant="ghost" disabled={refreshing} onClick={() => setRefresh((v) => v + 1)}>
            {refreshing ? 'در حال بروزرسانی…' : 'بروزرسانی'}
          </V2Button>
          {actions.edit ? (
            <V2Button variant="primary" onClick={() => setEditing(true)}>
              ویرایش مشتری
            </V2Button>
          ) : null}
        </div>
      </div>

      {editing ? (
        <CustomerEditDialog
          customer={customer}
          base={base}
          onCancel={() => setEditing(false)}
          onSaved={(updated) => {
            setEditing(false);
            if (updated.ref && updated.ref !== ref)
              navigate(`${base}/crm/customers/${encodeURIComponent(updated.ref)}`, {
                replace: true,
              });
            else setRefresh((value) => value + 1);
          }}
        />
      ) : null}

      <div className="c360-kpis">
        <KpiTile
          label="پیش‌فاکتورها"
          active={tab === 'quotes'}
          onClick={() => selectTab('quotes')}
          {...(quotes
            ? {
                value: fa(quotes.length),
                sub: `${fa(quotes.filter((q) => q.status === 'accepted').length)} پذیرفته‌شده`,
              }
            : { value: 'دسترسی ندارید', muted: true })}
        />
        <KpiTile
          label="سفارش‌ها"
          active={tab === 'orders'}
          onClick={() => selectTab('orders')}
          {...(orders
            ? {
                value: fa(orders.length),
                sub: `ارزش بدون لغو شده: ${formatAmount(sumRial(activeOrders.map((o) => o.totalRial)))} ${unitLabel}`,
              }
            : { value: 'دسترسی ندارید', muted: true })}
        />
        <KpiTile
          label="معاملات در جریان"
          active={tab === 'deals'}
          onClick={() => selectTab('deals')}
          {...(deals
            ? {
                value: fa(openDeals.length),
                sub: `برآورد: ${formatAmount(sumRial(openDeals.map((d) => d.amountRial)))} ${unitLabel}`,
              }
            : { value: 'دسترسی ندارید', muted: true })}
        />
        <KpiTile
          label="دریافتنی افتتاحیه (پیش‌نویس)"
          active={tab === 'invoicesAr'}
          onClick={() => selectTab('invoicesAr')}
          {...(receivables
            ? receivablesCapped
              ? {
                  value: `بیش از ${fa(OPENING_AR_READ_CAP)} مورد`,
                  sub: 'جمع کل نمایش داده نمی‌شود؛ فقط بخشی از موارد خوانده شده است',
                  muted: true,
                }
              : {
                  value: `${formatAmount(sumRial(receivables.map((r) => r.outstandingAmountRial)))} ${unitLabel}`,
                  sub: `${fa(receivables.length)} مورد، در انتظار تطبیق`,
                }
            : { value: 'دسترسی ندارید', muted: true })}
        />
        <KpiTile
          label="مالک حساب فروش"
          {...(!Object.hasOwn(profile, 'salesOwner')
            ? { value: '—', muted: true }
            : profile.salesOwner
              ? {
                  value: profile.salesOwner.name,
                  sub: profile.salesOwner.active ? undefined : 'غیرفعال',
                }
              : { value: 'تعیین نشده', muted: true })}
        />
      </div>

      <div className="c360-tabs" ref={tabsRef}>
        <V2Tabs
          activeId={tab}
          items={tabItems}
          onChange={(id) => selectTab(id as Customer360Tab)}
        />
      </div>

      {tab === 'overview' ? (
        <div className="c360-grid">
          <V2Card>
            <section className="c360-section" aria-label="مشخصات">
              <h2 className="c360-section__title">مشخصات</h2>
              <dl className="c360-fields">
                <Field label="نوع" value={customer.kind ? KIND_LABEL[customer.kind] : null} />
                <Field label="وضعیت">
                  {customer.status === 'active' ? (
                    <V2Badge tone="success">فعال</V2Badge>
                  ) : customer.status === 'inactive' ? (
                    <V2Badge tone="neutral">غیرفعال</V2Badge>
                  ) : (
                    '—'
                  )}
                </Field>
                <Field label="نقش‌ها">
                  {customer.roles?.map((role) => (
                    <span key={role} className="sk-tag">
                      {role === 'customer' ? 'مشتری' : 'تأمین‌کننده'}
                    </span>
                  )) ?? '—'}
                </Field>
                {Object.hasOwn(customer, 'nationalId') ? (
                  <Field
                    label={customer.kind === 'person' ? 'کد ملی' : 'شناسه ملی'}
                    value={customer.nationalId}
                    ltr
                    copy
                  />
                ) : null}
                {Object.hasOwn(customer, 'economicCode') ? (
                  <Field label="کد اقتصادی" value={customer.economicCode} ltr copy />
                ) : null}
                {Object.hasOwn(profile, 'registrationNumber') ? (
                  <Field label="شمارهٔ ثبت" value={profile.registrationNumber} ltr copy />
                ) : null}
                {Object.hasOwn(customer, 'postalCode') ? (
                  <Field label="کد پستی" value={customer.postalCode} ltr copy />
                ) : null}
              </dl>
            </section>
          </V2Card>
          <V2Card>
            <section className="c360-section" aria-label="نشانی و مسئول">
              <h2 className="c360-section__title">نشانی و مسئول</h2>
              <dl className="c360-fields">
                {Object.hasOwn(profile, 'province') ? (
                  <Field label="استان" value={profile.province} />
                ) : null}
                {Object.hasOwn(profile, 'city') ? <Field label="شهر" value={profile.city} /> : null}
                {Object.hasOwn(customer, 'address') ? (
                  <Field label="آدرس" value={customer.address} wide />
                ) : null}
                {Object.hasOwn(profile, 'salesOwner') ? (
                  <Field label="مالک حساب فروش">
                    {profile.salesOwner ? (
                      <span className="c360-owner">
                        <V2Avatar initials={initialsOf(profile.salesOwner.name)} size="sm" />
                        {profile.salesOwner.name}
                        {profile.salesOwner.active ? null : (
                          <V2Badge tone="neutral">غیرفعال</V2Badge>
                        )}
                      </span>
                    ) : (
                      'تعیین نشده'
                    )}
                  </Field>
                ) : null}
                {customer.updatedAt ? (
                  <Field label="آخرین ویرایش">
                    <span title={exactWhen(customer.updatedAt)}>
                      {relativeWhen(customer.updatedAt)}
                    </span>
                  </Field>
                ) : null}
                {customer.createdAt ? (
                  <Field label="تاریخ ایجاد">
                    <span title={exactWhen(customer.createdAt)}>
                      {relativeWhen(customer.createdAt)}
                    </span>
                  </Field>
                ) : null}
              </dl>
            </section>
          </V2Card>
        </div>
      ) : null}

      {tab === 'contacts' ? (
        sections.contacts.status === 'forbidden' && nothingToShowInContacts ? (
          <StatePanel variant="permission_denied" title="برای مشاهده این بخش دسترسی ندارید" />
        ) : nothingToShowInContacts ? (
          <V2EmptyState icon="phone" title="اطلاعات تماسی ثبت نشده است" compact />
        ) : (
          <div className="c360-grid">
            {primaryContactRows.length > 0 || extraPhones.length > 0 ? (
              <V2Card clipped>
                <section aria-label="شماره‌ها و ایمیل">
                  <h2 className="c360-section__title" style={{ padding: '16px 20px 0' }}>
                    تماس اصلی
                  </h2>
                  {primaryContactRows.map(([field, label]) => {
                    const value = (contact as Record<string, string | null | undefined>)[field];
                    return (
                      <div key={field} className="c360-contact">
                        <div className="c360-contact__main">
                          <span className="c360-contact__label">{label}</span>
                          <span className="c360-contact__value">
                            {value ? <span className="c360-ltr">{value}</span> : 'ثبت نشده'}
                          </span>
                        </div>
                        {value ? (
                          <div className="c360-contact__actions">
                            {field === 'email' ? (
                              <a
                                className="sk-btn sk-btn--secondary sk-btn--sm"
                                href={`mailto:${value}`}
                              >
                                ارسال ایمیل
                              </a>
                            ) : (
                              <a
                                className="sk-btn sk-btn--secondary sk-btn--sm"
                                href={phoneHref('tel', value)}
                              >
                                تماس
                              </a>
                            )}
                            {field === 'mobile' ? (
                              <a
                                className="sk-btn sk-btn--ghost sk-btn--sm"
                                href={phoneHref('sms', value)}
                              >
                                پیامک
                              </a>
                            ) : null}
                            <CopyButton text={value} label={label} />
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                  {extraPhones.map((phone, index) => (
                    <div key={`${phone.number}-${index}`} className="c360-contact">
                      <div className="c360-contact__main">
                        <span className="c360-contact__label">
                          {PHONE_KIND_LABEL[phone.kind] ?? 'سایر'}
                          {phone.label ? ` · ${phone.label}` : ''}
                        </span>
                        <span className="c360-contact__value">
                          <span className="c360-ltr">{phone.number}</span>
                        </span>
                      </div>
                      <div className="c360-contact__actions">
                        <a
                          className="sk-btn sk-btn--secondary sk-btn--sm"
                          href={phoneHref('tel', phone.number)}
                        >
                          تماس
                        </a>
                        <CopyButton text={phone.number} label="شماره" />
                      </div>
                    </div>
                  ))}
                </section>
              </V2Card>
            ) : null}
            {hasContactPerson && profile.contactPerson ? (
              <V2Card>
                <section className="c360-section" aria-label="شخص تماس">
                  <h2 className="c360-section__title">شخص تماس</h2>
                  <dl className="c360-fields">
                    <Field label="نام" value={profile.contactPerson.fullName} />
                    <Field label="سمت" value={profile.contactPerson.jobTitle} />
                    <Field label="موبایل" value={profile.contactPerson.mobile} ltr copy />
                  </dl>
                  {profile.contactPerson.mobile ? (
                    <div className="c360-contact__actions" style={{ marginTop: 14 }}>
                      <a
                        className="sk-btn sk-btn--secondary sk-btn--sm"
                        href={phoneHref('tel', profile.contactPerson.mobile)}
                      >
                        تماس
                      </a>
                      <a
                        className="sk-btn sk-btn--ghost sk-btn--sm"
                        href={phoneHref('sms', profile.contactPerson.mobile)}
                      >
                        پیامک
                      </a>
                    </div>
                  ) : null}
                </section>
              </V2Card>
            ) : null}
          </div>
        )
      ) : null}

      {tab === 'communications' ? <CustomerCommunicationsPanel base={base} partyRef={ref} /> : null}

      {tab === 'quotes' ? (
        quotes ? (
          <DocPanel
            rows={quotes}
            rowKey={(q) => q.ref}
            columns={quoteColumns}
            searchFields={(q) => [q.ref, quoteStatusLabel(q.status)]}
            searchPlaceholder="شمارهٔ پیش‌فاکتور یا وضعیت"
            filterOf={(q) => ({ id: q.status, label: quoteStatusLabel(q.status) })}
            amountOf={(q) => q.totalRial}
            refOf={(q) => q.ref}
            formatAmount={(d) => `${formatAmount(d)} ${unitLabel}`}
            totalLabel="مجموع نمایش‌داده‌شده"
            empty={{ icon: 'invoice', title: 'پیش‌فاکتوری ثبت نشده است' }}
          />
        ) : sections.quotes.status === 'forbidden' ? (
          <StatePanel variant="permission_denied" title="برای مشاهده این بخش دسترسی ندارید" />
        ) : (
          <UnavailablePanel tab="quotes" base={base} />
        )
      ) : null}

      {tab === 'orders' ? (
        orders ? (
          <DocPanel
            rows={orders}
            rowKey={(o) => o.ref}
            columns={orderColumns}
            searchFields={(o) => [
              o.ref,
              orderStatusLabel(o.status),
              o.fulfillment.statusLabel,
              o.importedSourceCode,
            ]}
            searchPlaceholder="شمارهٔ سفارش، وضعیت یا فایل مبدأ"
            filterOf={(o) => ({ id: o.status, label: orderStatusLabel(o.status) })}
            amountOf={(o) => o.totalRial}
            refOf={(o) => o.ref}
            formatAmount={(d) => `${formatAmount(d)} ${unitLabel}`}
            totalLabel="مجموع نمایش‌داده‌شده"
            empty={{ icon: 'sales', title: 'سفارشی ثبت نشده است' }}
          />
        ) : sections.orders.status === 'forbidden' ? (
          <StatePanel variant="permission_denied" title="برای مشاهده این بخش دسترسی ندارید" />
        ) : (
          <UnavailablePanel tab="orders" base={base} />
        )
      ) : null}

      {tab === 'invoicesAr' ? (
        receivables ? (
          <DocPanel
            rows={receivables}
            capped={receivablesCapped}
            rowKey={(r, index) => `${r.sourceReference}-${index}`}
            columns={receivableColumns}
            searchFields={(r) => [r.sourceReference]}
            searchPlaceholder="شمارهٔ فایل مبدأ"
            amountOf={(r) => r.outstandingAmountRial}
            refOf={(r) => r.sourceReference}
            formatAmount={(d) => `${formatAmount(d)} ${unitLabel}`}
            totalLabel="مجموع مانده"
            note={
              <>
                <strong>دریافتنی افتتاحیه (پیش‌نویس).</strong> {RECEIVABLE_DRAFT_NOTICE}
              </>
            }
            empty={{ icon: 'invoice', title: 'دریافتنی افتتاحیه‌ای ثبت نشده است' }}
          />
        ) : sections.invoicesAr.status === 'forbidden' ? (
          <StatePanel variant="permission_denied" title="برای مشاهده این بخش دسترسی ندارید" />
        ) : (
          <UnavailablePanel tab="invoicesAr" base={base} />
        )
      ) : null}

      {tab === 'deals' ? (
        <DealsTab
          section={sections.deals}
          base={base}
          customerRef={ref}
          canCreate={actions.createDeal}
          unit={unit}
          unitLabel={unitLabel}
          formatAmount={formatAmount}
        />
      ) : null}

      {tab === 'activities' ? (
        <ActivitiesTab
          section={sections.activities}
          base={base}
          customerRef={ref}
          canCreate={actions.createActivity}
          onChanged={() => setRefresh((value) => value + 1)}
        />
      ) : null}

      {tab === 'notes' ? (
        <NotesTab
          section={sections.notes}
          base={base}
          customerRef={ref}
          canCreate={actions.createNote}
          onChanged={() => setRefresh((value) => value + 1)}
        />
      ) : null}

      {tab === 'changeHistory' ? (
        <ChangeHistoryTab section={sections.changeHistory} base={base} />
      ) : null}

      <div className="c360-footnote">
        اطلاعات تماس و هویت از پرونده همین طرف‌حساب خوانده می‌شود. اسناد واردشده تنها با وضعیت واقعی
        و مجوز مشاهده نمایش داده می‌شوند. مبالغ بر اساس تنظیم نمایش ({unitLabel}) نشان داده می‌شوند.
      </div>
    </div>
  );
}
